import { compareSync } from 'bcryptjs';
import { useStoreState } from '@/stores/store';
import { FC, useCallback, useEffect, useState } from 'react';
import { Icon } from '@/components/Icon';
import { StackScreenProps } from '@react-navigation/stack';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { View } from 'react-native';
import { Text } from '@/components/Text';
import PasswordGesture from '..';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { useFocusEffect } from '@react-navigation/native';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { useSidebarAnimation } from '@/components/ui/therapist/CollapsibleSidebar/SidebarAnimationContext';

import { styles } from './styles';
import { NavigationRoute } from '@sentry/react-native/dist/js/tracing/reactnavigation';
import { logInfo } from '@/utils/logHandling';

export const CheckPassword: FC<
  StackScreenProps<TherapistStackParamList, 'CheckPassword'>
> = ({ route, navigation }) => {
  const [checkPasswordState, setCheckPasswordState] = useState({
    message: 'Sperrmuster eingeben',
    status: 'normal',
  });
  const { eigentrainingLock, PASSWORD, downloadPatients } = useStoreState(
    (store) => ({
      PASSWORD: store.appLockPattern,
      downloadPatients: store.downloadPatients,
      eigentrainingLock: store.eigentrainingLock,
    }),
  );
  const routes: NavigationRoute[] = navigation.getState().routes;
  const prevRoute = routes[routes.length - 2];
  const [prevRouteName, setPrevRouteName] = useState<string | undefined>(
    prevRoute.name,
  );
  const { showNotification } = useNotification();
  const { setShowHeader } = useSidebarAnimation();
  const [unlocked, setUnlocked] = useState(false);

  useFocusEffect(
    useCallback(() => {
      return navigation.addListener('beforeRemove', (e) => {
        if (!unlocked) {
          e.preventDefault();
        } else {
          navigation.dispatch(e.data.action);
        }
      });
    }, [navigation, unlocked]),
  );

  useFocusEffect(
    useCallback(() => {
      const prev = routes[routes.length - 2];
      setPrevRouteName(prev?.name);
    }, [navigation]),
  );

  const backToSelfTraining = () => {
    if (
      eigentrainingLock &&
      prevRouteName &&
      prevRouteName === 'SelfTraining'
    ) {
      return (
        <View style={{ paddingBottom: 20 }}>
          <PressableNeolexon
            onPress={() => {
              setUnlocked(true);
              setTimeout(() => navigation.goBack());
            }}
            style={styles.selfTrainingButton}
            testID="back-to-self-training-button"
          >
            <Icon name="playCircleInverted" size={36} />
            <Text style={styles.selfTrainingButtonText}>
              Zurück zum Eigentraining
            </Text>
          </PressableNeolexon>
        </View>
      );
    }
  };

  const onEnd = (password: string) => {
    if (compareSync(password, PASSWORD)) {
      setUnlocked(true);

      const beforePrevRoute = routes[routes.length - 3];

      // This timeout is necessary to trigger the navigation reset after the state update
      // without it, the navigation to the Home screen doesn't work on all devices.
      setTimeout(() => {
        if (route.params?.blockBackFromSelfTraining) {
          const getLatestSelfTrainingParams = () => {
            const route = [...routes]
              .reverse()
              .find((r) => r.name === 'SelfTraining');
            return route?.params ?? {};
          };
          if (getLatestSelfTrainingParams().selectedPatient) {
            logInfo('Navigating back to patient profile from lock screen');
            navigation.navigate('PatientProfile', {
              id: getLatestSelfTrainingParams().selectedPatient?._id,
              patients: downloadPatients,
            });
          } else {
            logInfo('Navigating to Home screen from lock screen');
            navigation.navigate('Home');
          }
        } else if (prevRoute && prevRoute.name !== 'Privacy') {
          logInfo(`Going back to ${prevRoute.name} from lock screen`);
          navigation.goBack();
        } else if (
          prevRoute &&
          prevRoute.name === 'Privacy' &&
          beforePrevRoute
        ) {
          logInfo(`Navigating to ${beforePrevRoute.name} from lockscreen`);
          navigation.navigate(
            beforePrevRoute.name as any,
            beforePrevRoute.params,
          );
        } else {
          logInfo('Resetting navigation to Home screen from lock screen');
          navigation.reset({
            index: 0,
            routes: [{ name: 'Home' }],
          });
        }
      });
    } else {
      setUnlocked(false);
      setCheckPasswordState({
        status: 'wrong',
        message: 'Sperrmuster ist falsch, versuchen Sie es erneut',
      });
    }
  };

  const onStart = () => {
    setCheckPasswordState({
      status: 'normal',
      message: 'Sperrmuster eingeben',
    });
  };

  useEffect(() => {
    logInfo('Showing lock screen', {
      lastVisitedPage: prevRoute?.name,
      route: route.params,
    });
  }, []);

  useFocusEffect(() => {
    setShowHeader(false);

    return () => {
      setShowHeader(true);
    };
  });

  return (
    <View style={styles.container} testID="lock-screen">
      {backToSelfTraining()}
      <View style={{ maxHeight: 380, flex: 1 }}>
        <PasswordGesture
          status={checkPasswordState.status}
          message={checkPasswordState.message}
          onStart={onStart}
          onEnd={onEnd}
          innerCircle={true}
          outerCircle={true}
          showNotification={showNotification}
        />
        <View style={styles.btnContainer}>
          <PressableNeolexon
            onPress={() => {
              navigation.navigate('PasswordUnlock');
            }}
            style={styles.flexOne}
            testID="forgot-pattern-button"
          >
            <Text style={styles.text}>Sperrmuster vergessen?</Text>
          </PressableNeolexon>
        </View>
      </View>
    </View>
  );
};
